'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import type { ReactNode } from 'react'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  CURRENT_MONTH_INDEX,
  LEAD_TIME_SIGNAL_DAYS,
  LEAD_TIMES,
  MY_TASKS,
  OVERDUE_COUNT,
  teamMember,
  weekdayIndexOf,
  YEAR_MONTH_LOAD,
} from '../demo-data'
import { useVisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, ColumnLabel } from './app-screen'
import styles from './dashboard-visual.module.css'
import { useScenario } from './use-scenario'

/** The three longest-running dossiers — the same list, and order, the signal card shows. */
const SIGNALS = LEAD_TIMES.slice(0, 3)

const YEAR_TOTAL = YEAR_MONTH_LOAD.reduce((sum, row) => sum + row.total, 0)

/**
 * The book year as two running totals: what the plan says should be finished by the end of each
 * month, and what actually is. Both as a share of the year's whole workload, so they meet at 100%
 * in December if the year is finished on time.
 *
 * A line, not the twelve bars this tile used to hold. The bars were a second copy of the chart on
 * the planning card two sections down — the same twelve columns in the same colours — and a month's
 * height says how busy it was, not whether the office is on schedule, which is the question a
 * dashboard is opened to answer. Where the two lines part is the answer.
 */
const TREND = (() => {
  let planned = 0
  let done = 0
  return YEAR_MONTH_LOAD.map((row) => {
    planned += row.total
    done += row.done
    return { planned: (planned / YEAR_TOTAL) * 100, done: (done / YEAR_TOTAL) * 100 }
  })
})()

/** The plot's own box. Percentages map straight onto it: x by month, y inverted so 100% is the top. */
const PLOT = { w: 100, h: 100 }
const pointX = (month: number) => (month / (TREND.length - 1)) * PLOT.w
const pointY = (pct: number) => PLOT.h - (pct / 100) * PLOT.h

const line = (values: readonly number[]) =>
  values
    .map((pct, month) => `${month === 0 ? 'M' : 'L'}${pointX(month).toFixed(2)} ${pointY(pct).toFixed(2)}`)
    .join(' ')

const PLANNED_LINE = line(TREND.map((point) => point.planned))

/**
 * The finished line stops at the month the office is in. Carried to December it ran flat across the
 * right-hand half of the plot, which reads as a forecast — a promise that nothing more gets done —
 * where the data only says that those months have not happened yet. Ending it where the year has
 * got to leaves the gap to the plan line as the plot's whole right-hand side, which is the thing
 * worth looking at.
 */
const DONE_TO_DATE = TREND.slice(0, CURRENT_MONTH_INDEX + 1).map((point) => point.done)
const DONE_LINE = line(DONE_TO_DATE)
/** The same line closed along the floor, so the finished share reads as an area and not only an edge. */
const DONE_AREA = `${DONE_LINE} L${pointX(DONE_TO_DATE.length - 1).toFixed(2)} ${PLOT.h} L0 ${PLOT.h} Z`

/**
 * Which month the pointer is reading, per beat. It rests on the running month — the one the office
 * is actually in — and mid-loop walks back through the two before it, which is what someone reading
 * a trend line does. Nothing about the chart changes while it walks: the figures are the chart's,
 * not the pointer's, so this adds a reader rather than a second story.
 */
const POINTER_MONTH = [
  CURRENT_MONTH_INDEX,
  CURRENT_MONTH_INDEX,
  CURRENT_MONTH_INDEX,
  CURRENT_MONTH_INDEX - 2,
  CURRENT_MONTH_INDEX - 1,
  CURRENT_MONTH_INDEX,
  CURRENT_MONTH_INDEX,
] as const

/**
 * A day moving on a dashboard that is always whole.
 *
 * The tiles used to fade up one per beat and strip back off again to restart, so a visitor was as
 * likely to catch the dashboard being taken apart as being built — and "Uren vandaag" ran
 * 6:00 → 7:30 → 6:00, which read as a correction rather than a reset. All four tiles are now present
 * in every frame and the story is what changes inside them: a task gets ticked, the hours climb, a
 * lead time comes down.
 */
const BEAT_HOLDS = [
  1500, // 0  the day as it stands — 6:00 booked, three tasks open, the longest dossier at 112 days
  950, //  1  a task is ticked off and the count drops
  1100, // 2  the hours climb to 7:30 and the day bar fills
  850, //  3  the trend gets read back: the pointer steps to mei
  850, //  4  and on to jun
  1200, // 5  and back to jul, the month being worked
  1300, // 6  the longest lead time comes down and the list reorders; held, then the day starts over
] as const

const REST_BEAT = 0 // the day at its start, which is what "de dag begint met overzicht" promises

const TASK_DONE_FROM = 1
const HOURS_FINAL_FROM = 2
const LEAD_TIME_RESOLVED_FROM = 6

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive widget state. */
function effectiveBeat(beat: number | null): number {
  return beat ?? REST_BEAT
}

function hoursLabel(effBeat: number): string {
  return effBeat >= HOURS_FINAL_FROM ? '7:30' : '6:00'
}

/** How much of the planned day is booked — the tile's own progress, not a separate fact. */
function hoursFraction(effBeat: number): number {
  return effBeat >= HOURS_FINAL_FROM ? 7.5 / 8 : 6 / 8
}

/**
 * One dashboard tile. Every tile is built from this, so the gap between a tile's caption and its
 * content is the same in all four — it comes from `ColumnLabel` and nowhere else.
 */
function Widget({
  label,
  count,
  countColor,
  children,
}: {
  label: string
  count?: ReactNode
  countColor?: string
  children: ReactNode
}) {
  return (
    <div
      className={clsx(styles.widget, styles.widgetVisible, 'flex min-h-0 flex-col rounded-[8px] border p-3')}
      style={{ borderColor: 'var(--au-border)' }}
    >
      <ColumnLabel
        right={
          count !== undefined ? (
            <span
              className="shrink-0 text-[11px] font-bold whitespace-nowrap tabular-nums"
              style={{ color: countColor ?? 'var(--au-accent)' }}
            >
              {count}
            </span>
          ) : undefined
        }
      >
        {label}
      </ColumnLabel>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  )
}

/**
 * Modules card — office dashboard whose 2×2 widget grid "lands" in sequence: Uren vandaag,
 * Maand, Mijn taken and Doorlooptijd each fade up on their own beat, with the hours counter
 * ticking up from 0:00 to its final 7:30. Driven by `useScenario`'s 10-beat loop; `beat === null`
 * (SSR, no-JS, `prefers-reduced-motion: reduce`) renders the resolved rest state: all 4 widgets
 * fully filled.
 */
export function DashboardVisual({ className }: { className?: string }) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)

  const taskDone = effBeat >= TASK_DONE_FROM
  const leadTimeResolved = effBeat >= LEAD_TIME_RESOLVED_FROM
  const pointerMonth = POINTER_MONTH[effBeat] ?? CURRENT_MONTH_INDEX

  return (
    <AppUiVisual label={t.screen.dashboard.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="dashboard" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.dashboard.title}
            detail={fmt.weekdayDate(17, weekdayIndexOf(17))}
            right={
              <span className="text-[10.5px]" style={{ color: 'var(--au-text2)' }}>
                {t.screen.dashboard.goodMorning}, {teamMember('MV').firstName}
              </span>
            }
          />

          <div className={clsx(styles.grid, 'min-h-0 flex-1 p-2')}>
            <Widget label={t.screen.dashboard.hoursToday}>
              <div className={clsx(styles.dayBody, 'flex flex-1 flex-col justify-center gap-2')}>
                {/* Side by side on a phone, stacked above it. Stacked in a quarter-height tile the
                    two lines and the bar left nothing between them; beside the figure, the planned
                    total is the caption it already was. */}
                <span className={styles.dayHead}>
                  <span
                    className={clsx(styles.dayFigure, 'leading-none font-bold whitespace-nowrap tabular-nums')}
                    style={{ color: 'var(--au-accent)' }}
                  >
                    {hoursLabel(effBeat)}
                  </span>
                  <span className={styles.dayPlanned}>{t.screen.dashboard.ofPlanned}</span>
                </span>
                {/* The same number as a bar. The tile was one figure floating in a large white box;
                    this gives the climb something to move and the box something to hold. */}
                <span className={styles.dayTrack}>
                  <span className={styles.dayFill} style={{ width: `${hoursFraction(effBeat) * 100}%` }} />
                </span>
              </div>
            </Widget>

            {/* The chart runs across a book year, not a month — the caption said "Maand" while
                showing twelve columns. The year named is the one being worked on: the firm is in
                2026, closing 2025. */}
            <Widget label={t.period.closingBookYear} count={`${Math.round(TREND[CURRENT_MONTH_INDEX].done)}%`}>
              <div className={clsx(styles.trendWrap, 'min-h-0 flex-1')}>
                {/* The plot, and the pointer over it, share one box. They did not: the pointer was
                    positioned against the padded wrapper while the line was drawn inside the
                    padding, so it sat a few pixels off its own line at every month. */}
                <div className={styles.plot}>
                  <svg
                    className={styles.trendSvg}
                    viewBox={`0 0 ${PLOT.w} ${PLOT.h}`}
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    {/* Non-scaling strokes throughout: the viewBox is stretched to the tile, so a
                      scaled stroke would be thicker across than down. */}
                    <path className={styles.trendArea} d={DONE_AREA} />
                    <path className={styles.trendPlanned} d={PLANNED_LINE} vectorEffect="non-scaling-stroke" />
                    <path className={styles.trendDone} d={DONE_LINE} vectorEffect="non-scaling-stroke" />
                  </svg>

                  {/* The pointer is an HTML layer over the plot rather than SVG geometry: it has to
                    travel between two months smoothly, and a percentage position transitions where
                    a `cx` attribute does not, reliably. */}
                  <span
                    className={styles.trendMarker}
                    style={{
                      left: `${pointX(pointerMonth)}%`,
                      top: `${pointY(TREND[pointerMonth].done)}%`,
                    }}
                    aria-hidden="true"
                  />
                  <span
                    className={styles.trendTip}
                    style={{
                      left: `${pointX(pointerMonth)}%`,
                      top: `${pointY(TREND[pointerMonth].done)}%`,
                    }}
                  >
                    {t.monthsShort[pointerMonth]} <b>{Math.round(TREND[pointerMonth].done)}%</b>
                  </span>
                </div>
              </div>
            </Widget>

            {/* Every open task, not a preview of them: the count above is the length of this list,
                so showing fewer rows than it counts would make the tile contradict itself. */}
            <Widget label={t.screen.dashboard.myTasks} count={MY_TASKS.length - (taskDone ? 1 : 0)}>
              {MY_TASKS.map((task, taskIndex) => (
                <div key={t.content.myTasks[taskIndex]} className="flex min-h-0 min-w-0 flex-1 items-center gap-2">
                  <span
                    className={clsx(styles.checkbox, taskIndex === 0 && taskDone && styles.checkboxDone)}
                    style={{ borderColor: 'var(--au-border)' }}
                  >
                    {taskIndex === 0 && taskDone ? '✓' : null}
                  </span>
                  {/* Client on its own line: on one line the longest of these ran past the tile,
                      and the name is the part that would have been cut. */}
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      className={clsx(
                        'truncate text-[11px] font-medium',
                        taskIndex === 0 && taskDone && styles.taskDone,
                      )}
                      style={{ color: 'var(--au-text)' }}
                    >
                      {t.content.myTasks[taskIndex]}
                    </span>
                    {task.nr && (
                      <span
                        className={clsx(styles.taskClient, 'truncate text-[9.5px]')}
                        style={{ color: 'var(--au-text2)' }}
                      >
                        {client(task.nr).short}
                      </span>
                    )}
                  </span>
                  <span
                    className="shrink-0 text-[10px] font-medium whitespace-nowrap tabular-nums"
                    style={{ color: 'var(--au-text2)' }}
                  >
                    {task.done}/{task.total}
                  </span>
                </div>
              ))}
            </Widget>

            {/* The longest-running dossiers, straight off the shared lead-time list, so the count
                above and the rows below can never tell different stories. */}
            <Widget
              label={t.screen.dashboard.leadTime}
              count={leadTimeResolved ? OVERDUE_COUNT - 1 : OVERDUE_COUNT}
              countColor="var(--au-sig)"
            >
              {SIGNALS.map((item, index) => {
                // The longest-running dossier is picked up and comes down under the signal
                // threshold. Nothing else in the tile moves, so the eye goes to the one that does.
                // Two short of the threshold: under it, but still clear of the row below, since two
                // identical figures in a list sorted by size read as a fault.
                const days = index === 0 && leadTimeResolved ? LEAD_TIME_SIGNAL_DAYS - 2 : item.days
                return (
                  <div key={item.nr} className="flex min-h-0 min-w-0 flex-1 items-center justify-between gap-2">
                    <span
                      className="min-w-0 flex-1 truncate text-[11px] font-medium"
                      style={{ color: 'var(--au-text)' }}
                    >
                      {client(item.nr).short}
                    </span>
                    <span
                      className={clsx(styles.leadDays, 'shrink-0 text-[11px] font-bold whitespace-nowrap tabular-nums')}
                      style={{ color: days > LEAD_TIME_SIGNAL_DAYS ? 'var(--au-sig)' : 'var(--au-text)' }}
                    >
                      {days} dgn
                    </span>
                  </div>
                )
              })}
            </Widget>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
